import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { AuthorizationService, LIMIT_TO_SHOW_CREDIT_WARNING, ReportService, TranslationService } from '@mirage-projects/cw-commons';
import { CompanyReport, LicencesReport, ReportData, ReportDataId, UserReport } from '@mirage-projects/cw-models';

/** This components displays the reports with the company info and user info */
@Component({
    selector: 'cw-reports-widget',
    templateUrl: './reports-widget.component.html',
    styleUrls: ['./reports-widget.component.scss'],
    standalone: false,
})
export class ReportsWidgetComponent implements OnInit {
    /** Shows the loader */
    isLoading = true;
    companyReportData!: ReportData[];
    userReportData!: ReportData[];
    companyReportName = '';
    userReportName = '';
    showWarningMsg = false;
    msgError: string | undefined = undefined;

    constructor(
        private router: Router,
        private reportService: ReportService,
        private authorizationService: AuthorizationService,
        private translationService: TranslationService
    ) {}

    ngOnInit(): void {
        this.initializeReportData();
    }

    navigateToReports(): void {
        this.router.navigate(['/reports']);
    }

    private initializeReportData(): void {
        const { userId } = this.authorizationService.getCurrentUser();

        if (!userId) {
            this.msgError = 'DASHBOARD.ERROR_REPORTS';
            this.isLoading = false;
            return;
        }

        this.reportService.getByScopeAndUserId('user', userId).subscribe((report) => {
            this.isLoading = false;
            const userReport = report[userId] as UserReport;
            this.userReportData = this.getReportData(userReport, report.licences);
            this.userReportName = this.translationService.translate('LOCALE.ORDER_NAME', {
                firstName: userReport.userFirstName,
                lastName: userReport.userLastName,
            });

            const companyReport = report.company;
            this.companyReportName = companyReport.name;
            this.companyReportData = this.getReportData(companyReport, report.licences);

            const companyAvailableCredits = report.licences.maxprocessing - companyReport.processingCost;
            this.showWarningMsg = companyAvailableCredits < LIMIT_TO_SHOW_CREDIT_WARNING;
        });
    }

    private getReportData(objectReport: UserReport | CompanyReport, licences: LicencesReport): ReportData[] {
        const transferQuota = licences.maxtransfer;
        const storageQuota = licences.maxstorage;
        const processingQuota = licences.maxprocessing;

        let reportData: ReportData[] = [
            {
                id: 'transfer',
                label: 'REPORTS.TRANSFER',
                value: objectReport.transferSize,
                maxValue: transferQuota,
                percentage: (objectReport.transferSize / transferQuota) * 100,
            },
            {
                id: 'storage',
                label: 'REPORTS.STORAGE',
                value: objectReport.storageSize,
                maxValue: storageQuota,
                percentage: (objectReport.storageSize / storageQuota) * 100,
            },
            {
                id: 'processing',
                label: 'REPORTS.PROCESSING',
                value: objectReport.processingCost,
                maxValue: processingQuota,
                percentage: (objectReport.processingCost / processingQuota) * 100,
                unit: 'REPORTS.CREDITS',
            },
        ];

        if (processingQuota <= 0 && !this.authorizationService.isSupportUser()) {
            reportData = reportData.filter((report) => report.id !== ReportDataId.PROCESSING);
        }

        return reportData;
    }
}
